import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Save, Send, CheckCircle, Info, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDERS = {
  gmail: {
    label: 'Gmail',
    host: 'smtp.gmail.com',
    port: 587,
    color: '#EA4335',
    icon: '✉',
    description_ro: 'Folosiți un App Password Gmail (nu parola contului). Activați mai întâi 2FA pe contul Google.',
    description_en: 'Use a Gmail App Password (not your account password). Enable 2FA on your Google account first.',
    password_label_ro: 'App Password Gmail',
    password_label_en: 'Gmail App Password',
    password_hint_ro: 'Generați din: Google Account → Securitate → Parole de aplicație',
    password_hint_en: 'Generate at: Google Account → Security → App Passwords',
  },
  outlook: {
    label: 'Outlook / Microsoft 365',
    host: 'smtp.office365.com',
    port: 587,
    color: '#0078D4',
    icon: '✉',
    description_ro: 'Utilizați adresa și parola contului Microsoft / Outlook.',
    description_en: 'Use your Microsoft / Outlook account email and password.',
    password_label_ro: 'Parolă Outlook',
    password_label_en: 'Outlook Password',
    password_hint_ro: 'Parola contului Microsoft / Office 365',
    password_hint_en: 'Your Microsoft / Office 365 account password',
  },
};

function ProviderCard({ providerKey, currentSettings, onSaved }) {
  const { language } = useLanguage();
  const { token } = useAuth();
  const isRo = language === 'ro';
  const provider = PROVIDERS[providerKey];
  const isActive = currentSettings?.provider === providerKey;

  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [fromName, setFromName] = useState('EcoWash');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isActive && currentSettings) {
      setSmtpUser(currentSettings.smtp_user || '');
      setFromName(currentSettings.from_name || 'EcoWash');
    }
  }, [isActive, currentSettings]);

  const handleSave = async () => {
    if (!smtpUser.trim()) {
      toast.error(isRo ? 'Adresa de email este obligatorie.' : 'Email address is required.');
      return;
    }
    if (!smtpPassword.trim()) {
      toast.error(isRo ? 'Parola este obligatorie.' : 'Password is required.');
      return;
    }
    if (!fromName.trim()) {
      toast.error(isRo ? 'Numele expeditorului este obligatoriu.' : 'Sender name is required.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/smtp-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          provider: providerKey,
          smtp_user: smtpUser.trim(),
          smtp_password: smtpPassword,
          from_name: fromName.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Eroare');
      toast.success(data.message || (isRo ? 'Configurare salvată' : 'Settings saved'));
      setSmtpPassword('');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`smtp-card-${providerKey}`}
      className={`rounded-xl border-2 p-6 transition-all ${
        isActive
          ? 'border-primary bg-primary/5'
          : 'border-border bg-card hover:border-primary/40'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold"
            style={{ backgroundColor: provider.color }}
          >
            {provider.label.charAt(0)}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{provider.label}</h3>
            <p className="text-xs text-muted-foreground">
              {provider.host}:{provider.port} · TLS
            </p>
          </div>
        </div>
        {isActive && (
          <span
            data-testid={`smtp-active-badge-${providerKey}`}
            className="flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full"
          >
            <CheckCircle className="w-3 h-3" />
            {isRo ? 'Activ' : 'Active'}
          </span>
        )}
      </div>

      {/* Info box */}
      <div className="flex gap-2 bg-muted/60 rounded-lg p-3 mb-5 text-sm text-muted-foreground">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
        <span>{isRo ? provider.description_ro : provider.description_en}</span>
      </div>

      {/* Fields */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground mb-1">
              {isRo ? 'Server SMTP' : 'SMTP Host'}
            </Label>
            <Input value={provider.host} readOnly className="bg-muted/40 text-sm cursor-default" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1">Port</Label>
            <Input value={provider.port} readOnly className="bg-muted/40 text-sm cursor-default" />
          </div>
        </div>

        <div>
          <Label htmlFor={`user-${providerKey}`} className="text-sm font-medium mb-1.5 block">
            {isRo ? 'Adresă email expeditor' : 'Sender email address'} *
          </Label>
          <Input
            id={`user-${providerKey}`}
            data-testid={`smtp-user-${providerKey}`}
            type="email"
            placeholder={providerKey === 'gmail' ? 'you@gmail.com' : 'you@company.com'}
            value={smtpUser}
            onChange={(e) => setSmtpUser(e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor={`pass-${providerKey}`} className="text-sm font-medium mb-1.5 block">
            {isRo ? provider.password_label_ro : provider.password_label_en} *
          </Label>
          <div className="relative">
            <Input
              id={`pass-${providerKey}`}
              data-testid={`smtp-password-${providerKey}`}
              type={showPassword ? 'text' : 'password'}
              placeholder={isActive ? '••••••••  (lăsați gol pentru a păstra)' : ''}
              value={smtpPassword}
              onChange={(e) => setSmtpPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {isRo ? provider.password_hint_ro : provider.password_hint_en}
          </p>
        </div>

        <div>
          <Label htmlFor={`name-${providerKey}`} className="text-sm font-medium mb-1.5 block">
            {isRo ? 'Nume expeditor' : 'Sender name'} *
          </Label>
          <Input
            id={`name-${providerKey}`}
            data-testid={`smtp-from-name-${providerKey}`}
            placeholder="EcoWash"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {isRo
              ? `Emailul va apărea ca: "${fromName} <${smtpUser || 'email@exemplu.com'}>"` 
              : `Email will appear as: "${fromName} <${smtpUser || 'email@example.com'}>"`}
          </p>
        </div>
      </div>

      <Button
        data-testid={`smtp-save-btn-${providerKey}`}
        className="mt-5 w-full gap-2"
        onClick={handleSave}
        disabled={saving}
      >
        <Save className="w-4 h-4" />
        {saving
          ? (isRo ? 'Se salvează...' : 'Saving...')
          : (isRo ? `Salvează configurare ${provider.label}` : `Save ${provider.label} config`)}
      </Button>
    </div>
  );
}

export default function EmailSettings() {
  const { language } = useLanguage();
  const { token, user } = useAuth();
  const isRo = language === 'ro';

  const [currentSettings, setCurrentSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/smtp-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentSettings(data.configured ? data : null);
        if (data.configured && !testEmail) {
          setTestEmail(user?.email || '');
        }
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    if (user?.email) setTestEmail(user.email);
  }, []);

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      toast.error(isRo ? 'Introduceți o adresă de email pentru test.' : 'Enter a test email address.');
      return;
    }
    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/smtp-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ test_email: testEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Eroare');
      toast.success(data.message || (isRo ? 'Email de test trimis!' : 'Test email sent!'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isRo ? 'Setări Email (SMTP)' : 'Email Settings (SMTP)'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isRo
              ? 'Configurați serverul de email pentru trimiterea codurilor de verificare utilizatori.'
              : 'Configure the email server for sending user verification codes.'}
          </p>
        </div>
      </div>

      {/* Current active config summary */}
      {!loadingSettings && currentSettings && (
        <div
          data-testid="smtp-current-config"
          className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 text-sm"
        >
          <CheckCircle className="w-4 h-4 text-primary shrink-0" />
          <span className="text-foreground">
            {isRo ? 'Configurare activă: ' : 'Active config: '}
            <strong>{PROVIDERS[currentSettings.provider]?.label}</strong>
            {' — '}
            <span className="text-muted-foreground">{currentSettings.smtp_user}</span>
          </span>
        </div>
      )}

      {/* Provider Cards */}
      {loadingSettings ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="grid gap-5">
          {Object.keys(PROVIDERS).map((key) => (
            <ProviderCard
              key={key}
              providerKey={key}
              currentSettings={currentSettings}
              onSaved={fetchSettings}
            />
          ))}
        </div>
      )}

      {/* Test Email Section */}
      <div
        data-testid="smtp-test-section"
        className="rounded-xl border border-border bg-card p-6"
      >
        <h2 className="font-semibold text-foreground mb-1">
          {isRo ? 'Trimite email de test' : 'Send test email'}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {isRo
            ? 'Verificați că setările SMTP funcționează corect trimițând un email de test.'
            : 'Verify your SMTP settings by sending a test email.'}
        </p>
        <div className="flex gap-3">
          <Input
            data-testid="smtp-test-email-input"
            type="email"
            placeholder={isRo ? 'Adresă email destinatar test' : 'Test recipient email'}
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="flex-1"
          />
          <Button
            data-testid="smtp-send-test-btn"
            onClick={handleSendTest}
            disabled={sendingTest || !currentSettings}
            variant="outline"
            className="gap-2 shrink-0"
          >
            <Send className="w-4 h-4" />
            {sendingTest
              ? (isRo ? 'Se trimite...' : 'Sending...')
              : (isRo ? 'Trimite test' : 'Send test')}
          </Button>
        </div>
        {!currentSettings && (
          <p className="text-xs text-amber-500 mt-2">
            {isRo
              ? 'Salvați mai întâi o configurare SMTP pentru a putea trimite un email de test.'
              : 'Save an SMTP configuration first to be able to send a test email.'}
          </p>
        )}
      </div>
    </div>
  );
}
